import { Target, TrendingUp, Globe2, Lightbulb, Users, Rocket } from 'lucide-react';
import { Link } from 'react-router-dom';

const OurStory = () => {
  const storyThemes = [
    {
      icon: <Target className="w-10 h-10 text-[#007BFF]" />,
      title: 'Market Gap Identification',
      description: 'Energy markets faced critical information fragmentation. Decision-makers across corporates, investors, and policy institutions required structured, credible intelligence to navigate increasing complexity—but existing solutions were insufficient.'
    },
    {
      icon: <Lightbulb className="w-10 h-10 text-[#007BFF]" />,
      title: 'Solution Development',
      description: 'Enerlligence was founded to transform fragmented energy information into decision-useful intelligence. We built analytical frameworks, data infrastructure, and research capabilities to deliver institutional-grade insights across global energy markets.'
    },
    {
      icon: <Users className="w-10 h-10 text-[#007BFF]" />,
      title: 'Client-Focused Approach',
      description: 'From inception, our model centered on client needs. We deliver intelligence that directly supports strategic planning, investment decisions, risk assessment, and policy development—focusing on material impact rather than generic market commentary.'
    },
    {
      icon: <Rocket className="w-10 h-10 text-[#007BFF]" />,
      title: 'Scaling & Growth',
      description: 'We expanded coverage to 200+ countries, built multidisciplinary analytical teams, and developed platform capabilities that enable rapid intelligence delivery. Our growth reflects increasing demand for independent, structured energy analysis.'
    }
  ];

  const keyMilestones = [
    {
      title: 'Foundation & Vision',
      description: 'Established to address the need for independent, structured energy intelligence in an increasingly complex global landscape'
    },
    {
      title: 'Platform Development',
      description: 'Built proprietary data infrastructure and analytical frameworks to deliver institutional-grade market insights'
    },
    {
      title: 'Geographic Expansion',
      description: 'Scaled intelligence coverage across major energy markets, emerging economies, and transition-critical regions'
    },
    {
      title: 'Client Base Growth',
      description: 'Developed trusted relationships with leading energy companies, financial institutions, and policy organizations worldwide'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1F7A4F] via-[#2C6E73] to-[#284A86] text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 whitespace-nowrap">Our Story</h1>
            <p className="text-base lg:text-lg text-white/90 leading-relaxed">
              Building the energy intelligence infrastructure for strategic decision-making in global energy markets.
            </p>
          </div>
        </div>
      </section>

      {/* Why Enerlligence Was Created - Aligned Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Main Content */}
            <div className="flex flex-col justify-between">
              <div>
                <h2 className="text-3xl font-bold text-[#1F3C88] mb-6">Why Enerlligence Was Created</h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  The global energy landscape has become increasingly complex, characterized by supply chain dynamics, geopolitical volatility, technological disruption, and accelerating transition imperatives.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  Decision-makers across energy companies, financial institutions, and policy organizations required structured, credible intelligence to navigate this complexity with confidence.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed">
                  Enerlligence was founded to provide precisely this capability: transforming fragmented information into decision-useful intelligence.
                </p>
              </div>
            </div>

            {/* Side Card - Aligned Height */}
            <div className="bg-slate-50 p-10 rounded-lg border border-slate-200 flex flex-col justify-between">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <Target className="w-8 h-8 text-[#007BFF] flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-[#1F3C88] mb-2">Strategic Imperative</h3>
                    <p className="text-lg text-[#1F3C88]">Structured intelligence for complex energy systems</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <TrendingUp className="w-8 h-8 text-[#007BFF] flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-[#1F3C88] mb-2">Market Need</h3>
                    <p className="text-lg text-[#1F3C88]">Decision-grade analysis for institutional clients</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Globe2 className="w-8 h-8 text-[#007BFF] flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-[#1F3C88] mb-2">Global Perspective</h3>
                    <p className="text-lg text-[#1F3C88]">Comprehensive coverage across energy markets</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Journey - Startup Narrative */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1F3C88] mb-3">Our Journey</h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              From founding vision to global intelligence platform—the evolution of Enerlligence Analytics
            </p>
          </div>

          {/* Startup Story Narrative */}
          <div className="max-w-5xl mx-auto mb-16">
            <div className="bg-white p-10 rounded-lg border border-slate-200 shadow-sm">
              <h3 className="text-2xl font-bold text-[#1F3C88] mb-6">The Founding Vision</h3>
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                Enerlligence Analytics emerged from recognition of a fundamental market gap: the energy sector's need for independent, structured intelligence that could support high-stakes strategic and investment decisions. While information was abundant, decision-useful analysis was scarce.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed mb-6">
                Our founders—veterans of energy markets, financial analysis, and strategic consulting—understood that institutional decision-makers required more than data feeds or generic commentary. They needed rigorous frameworks, transparent methodologies, and analytical depth that translated complex market dynamics into actionable insight.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                This insight became Enerlligence's founding principle: deliver intelligence that materially impacts strategic outcomes, backed by analytical rigor and institutional credibility.
              </p>
            </div>
          </div>

          {/* Thematic Journey Blocks */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {storyThemes.map((theme, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-lg border-2 border-slate-200 hover:border-[#007BFF] transition-all duration-300"
              >
                <div className="mb-6">{theme.icon}</div>
                <h3 className="text-xl font-bold text-[#1F3C88] mb-4">{theme.title}</h3>
                <p className="text-slate-600 leading-relaxed">{theme.description}</p>
              </div>
            ))}
          </div>

          {/* Key Milestones */}
          <div className="bg-white p-10 rounded-lg border border-slate-200">
            <h3 className="text-2xl font-bold text-[#1F3C88] mb-8 text-center">Building Institutional Capabilities</h3>
            <div className="grid md:grid-cols-2 gap-8">
              {keyMilestones.map((milestone, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="w-3 h-3 bg-[#007BFF] rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="font-semibold text-[#1F3C88] mb-2">{milestone.title}</h4>
                    <p className="text-slate-600 leading-relaxed">{milestone.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Looking Forward - White Background */}
      <section className="py-20 bg-white border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Rocket className="w-16 h-16 text-[#007BFF] mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-[#1F3C88] mb-6">Looking Forward</h2>
          <p className="text-xl text-slate-700 leading-relaxed mb-8">
            As global energy systems continue to transform, Enerlligence remains committed to expanding intelligence coverage, enhancing analytical methodologies, and delivering the insights that power strategic advantage.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed mb-10">
            Our mission remains constant: providing trusted, structured, forward-looking intelligence for better energy decisions. We continue investing in team expertise, data infrastructure, and analytical capabilities to serve the evolving needs of our clients.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-[#007BFF] text-white font-semibold rounded-md hover:bg-[#0066DD] transition-all duration-200 shadow-lg"
          >
            Partner With Us
          </Link>
        </div>
      </section>
    </div>
  );
};

export default OurStory;
